import { fetchConfig, onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, VariantUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import { syncNojuPdpStickyAtc } from '@theme/noju-pdp-sticky-atc';

/**
 * NOJU PDP media gallery — horizontal scroll + variant sync.
 */
class NojuPdpMedia extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #counterCurrent = null;

  /** @type {MediaQueryList | null} */
  #desktopGalleryQuery = null;

  /** @type {(() => void) | null} */
  #onDesktopGalleryChange = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-noju-pdp-gallery-track]');
    this.#counterCurrent = this.querySelector('[data-noju-pdp-gallery-current]');
    this.#prevButton = this.querySelector('[data-noju-pdp-gallery-prev]');
    this.#nextButton = this.querySelector('[data-noju-pdp-gallery-next]');
    this.#desktopGalleryQuery = window.matchMedia('(min-width: 990px)');
    this.#onDesktopGalleryChange = () => {
      this.#updateCounter();
    };

    this.#track?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#prevButton?.addEventListener('click', this.#onPrevClick);
    this.#nextButton?.addEventListener('click', this.#onNextClick);
    this.#desktopGalleryQuery.addEventListener('change', this.#onDesktopGalleryChange);

    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    this.#updateCounter();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#prevButton?.removeEventListener('click', this.#onPrevClick);
    this.#nextButton?.removeEventListener('click', this.#onNextClick);
    this.#desktopGalleryQuery?.removeEventListener('change', this.#onDesktopGalleryChange);

    const section = this.closest('.shopify-section');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  get #isDesktopGallery() {
    return this.#desktopGalleryQuery?.matches ?? false;
  }

  /** @type {HTMLButtonElement | null} */
  #prevButton = null;

  /** @type {HTMLButtonElement | null} */
  #nextButton = null;

  #onPrevClick = () => {
    this.#scrollToRelativeSlide(-1);
  };

  #onNextClick = () => {
    this.#scrollToRelativeSlide(1);
  };

  /** @param {number} direction */
  #scrollToRelativeSlide(direction) {
    if (this.#isDesktopGallery) return;
    if (!this.#track) return;

    const slides = /** @type {HTMLElement[]} */ ([...this.#track.querySelectorAll('[data-slide-index]')]);
    if (slides.length === 0) return;

    const slideWidth = slides[0].offsetWidth || 1;
    const currentIndex = Math.round(this.#track.scrollLeft / slideWidth);
    const nextIndex = Math.min(Math.max(currentIndex + direction, 0), slides.length - 1);
    const targetSlide = slides[nextIndex];

    if (!(targetSlide instanceof HTMLElement)) return;

    targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    this.#setActiveSlide(targetSlide);
    this.#updateCounter();
  }

  #onScroll = () => {
    this.#updateCounter();
  };

  /** @param {VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    const featuredMediaId = event.detail.resource?.featured_media?.id;
    if (!featuredMediaId || !this.#track) return;

    const targetSlide = this.#track.querySelector(`[data-media-id="${featuredMediaId}"]`);
    if (!(targetSlide instanceof HTMLElement)) return;

    if (this.#isDesktopGallery) {
      this.#setActiveSlide(targetSlide);
      return;
    }

    targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    this.#setActiveSlide(targetSlide);
    this.#updateCounter();
  };

  #updateCounter() {
    if (!this.#track) return;

    if (this.#isDesktopGallery) {
      const activeSlide = this.#track.querySelector('.noju-pdp__gallery-slide.is-active');
      if (activeSlide instanceof HTMLElement) {
        this.#setActiveSlide(activeSlide);
      }
      return;
    }

    if (!this.#counterCurrent) return;

    const slides = /** @type {HTMLElement[]} */ ([...this.#track.querySelectorAll('[data-slide-index]')]);
    if (slides.length === 0) return;

    const scrollLeft = this.#track.scrollLeft;
    const slideWidth = slides[0].offsetWidth || 1;
    const index = Math.round(scrollLeft / slideWidth);
    const clampedIndex = Math.min(Math.max(index, 0), slides.length - 1);

    this.#counterCurrent.textContent = String(clampedIndex + 1);
    this.#setActiveSlide(slides[clampedIndex]);
    this.#updateNavButtons(clampedIndex, slides.length);
  }

  /** @param {number} index @param {number} total */
  #updateNavButtons(index, total) {
    if (this.#prevButton) {
      this.#prevButton.disabled = index <= 0;
    }

    if (this.#nextButton) {
      this.#nextButton.disabled = index >= total - 1;
    }
  }

  /** @param {HTMLElement} slide */
  #setActiveSlide(slide) {
    this.querySelectorAll('.noju-pdp__gallery-slide.is-active').forEach((el) => {
      el.classList.remove('is-active');
    });
    slide.classList.add('is-active');
  }
}

if (!customElements.get('noju-pdp-media')) {
  customElements.define('noju-pdp-media', NojuPdpMedia);
}

/**
 * Core set block — opens a half-page size-picker sheet and adds the
 * matching Shopify Bundle variant to the cart.
 */
class NojuCoreSetAdd extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Record<string, { id: number, available: boolean, priceFormatted: string }>} */
  #combos = {};

  /** @type {string} */
  #currentSize = '';

  /** @type {number} */
  #closeGeneration = 0;

  connectedCallback() {
    this.#currentSize = this.dataset.defaultSize || '';

    const dataEl = this.querySelector('[data-core-set-data]');
    if (dataEl?.textContent) {
      try {
        this.#combos = JSON.parse(dataEl.textContent).combos || {};
      } catch {
        this.#combos = {};
      }
    }

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);

    const sheet = this.#getSheet();
    sheet?.addEventListener('click', this.#onSheetBackdropClick);
    sheet?.addEventListener('cancel', this.#onSheetCancel);
    sheet?.addEventListener('close', this.#onSheetClosed);

    if (sheet?.open) {
      sheet.close();
    }
    sheet?.classList.remove('is-closing');

    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    if (this.dataset.pendingOpen === 'true') {
      delete this.dataset.pendingOpen;
      queueMicrotask(() => this.#openSheet());
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    document.documentElement.classList.remove('noju-core-set-sheet-open');

    const sheet = this.#getSheet();
    sheet?.removeEventListener('click', this.#onSheetBackdropClick);
    sheet?.removeEventListener('cancel', this.#onSheetCancel);
    sheet?.removeEventListener('close', this.#onSheetClosed);

    const section = this.closest('.shopify-section');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #onSheetClosed = () => {
    this.#getSheet()?.classList.remove('is-closing');
    document.documentElement.classList.remove('noju-core-set-sheet-open');
  };

  #restartInnerAnimation() {
    const inner = this.#getSheetInner();
    if (!inner) return;

    inner.style.animation = 'none';
    void inner.offsetWidth;
    inner.style.animation = '';
  }

  /** @param {Event} event */
  #onSheetCancel = (event) => {
    event.preventDefault();
    this.#closeSheet();
  };

  /** @returns {HTMLDialogElement | null} */
  #getSheet() {
    const sheet = this.querySelector('[data-core-set-sheet]');
    return sheet instanceof HTMLDialogElement ? sheet : null;
  }

  /**
   * Track the size selected on the PDP so the sheet opens pre-selected.
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail.resource;
    const size = variant?.option1 ?? variant?.title;
    if (size) this.#currentSize = String(size);
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-core-set-open]')) {
      event.preventDefault();
      this.#openSheet();
      return;
    }

    if (target.closest('[data-core-set-close]')) {
      event.preventDefault();
      this.#closeSheet();
      return;
    }

    if (target.closest('[data-core-set-submit]')) {
      event.preventDefault();
      this.#addToCart();
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('[data-core-set-size-input]')) {
      this.#syncSheetState();
    }
  };

  /** Close when clicking the backdrop (outside the inner panel). */
  /** @param {MouseEvent} event */
  #onSheetBackdropClick = (event) => {
    if (event.target === this.#getSheet()) {
      this.#closeSheet();
    }
  };

  /** @returns {HTMLElement | null} */
  #getSheetInner() {
    const inner = this.#getSheet()?.querySelector('.noju-pdp__core-set-sheet-inner');
    return inner instanceof HTMLElement ? inner : null;
  }

  #openSheet() {
    const sheet = this.#getSheet();
    if (!sheet) return;

    if (sheet.open && !sheet.classList.contains('is-closing')) return;

    this.#currentSize = this.#getLiveSize();
    this.#preselectSizes();
    this.#syncSheetState();

    this.#closeGeneration += 1;
    sheet.classList.remove('is-closing');

    if (!sheet.open) {
      if (typeof sheet.showModal === 'function') {
        sheet.showModal();
      } else {
        sheet.setAttribute('open', '');
      }
    }

    document.documentElement.classList.add('noju-core-set-sheet-open');
    this.#restartInnerAnimation();
  }

  async #closeSheet() {
    const sheet = this.#getSheet();
    const inner = this.#getSheetInner();
    if (!sheet?.open || !inner || sheet.classList.contains('is-closing')) return;

    const generation = this.#closeGeneration + 1;
    this.#closeGeneration = generation;

    sheet.classList.add('is-closing');
    this.#restartInnerAnimation();

    await Promise.race([
      onAnimationEnd(inner, undefined, { subtree: false }),
      new Promise((resolve) => window.setTimeout(resolve, 400)),
    ]);

    if (this.#closeGeneration !== generation) return;

    if (sheet.open) {
      sheet.close();
    }

    sheet.classList.remove('is-closing');
    document.documentElement.classList.remove('noju-core-set-sheet-open');
  }

  /**
   * Read the size currently selected in the PDP buy box from the live DOM.
   * Survives Horizon's section morphs, unlike event-tracked state.
   * @returns {string}
   */
  #getLiveSize() {
    const section = this.closest('.shopify-section');
    const checked = section?.querySelector('.noju-pdp__size-options input:checked');
    if (!checked) return this.#currentSize;

    const text = checked
      .closest('.noju-pdp__size-cell')
      ?.querySelector('[data-key="variant-option-text"]')
      ?.textContent?.trim();

    return text || checked.value || this.#currentSize;
  }

  /** Pre-select both dimensions with the size chosen on the PDP. */
  #preselectSizes() {
    const size = this.#currentSize;

    [1, 2].forEach((dimension) => {
      const inputs = this.#getDimensionInputs(dimension);
      if (inputs.length === 0) return;

      const alreadyChecked = inputs.some((input) => input.checked);
      if (alreadyChecked && !size) return;

      const match = inputs.find((input) => input.value === size);
      (match || inputs[0]).checked = true;
    });
  }

  /**
   * @param {number} dimension
   * @returns {HTMLInputElement[]}
   */
  #getDimensionInputs(dimension) {
    return /** @type {HTMLInputElement[]} */ ([
      ...this.querySelectorAll(`[data-core-set-dimension="${dimension}"] [data-core-set-size-input]`),
    ]);
  }

  /**
   * @param {number} dimension
   * @returns {string}
   */
  #getSelectedSize(dimension) {
    return this.#getDimensionInputs(dimension).find((input) => input.checked)?.value || '';
  }

  /** @returns {{ id: number, available: boolean, priceFormatted: string } | undefined} */
  #getSelectedCombo() {
    const opt1 = this.#getSelectedSize(1);
    const opt2 = this.#getSelectedSize(2);
    if (!opt1 || !opt2) return undefined;
    return this.#combos[`${opt1}|${opt2}`];
  }

  /** Update pill availability, selected-size labels, price and submit state. */
  #syncSheetState() {
    const opt1 = this.#getSelectedSize(1);
    const opt2 = this.#getSelectedSize(2);

    this.#getDimensionInputs(1).forEach((input) => {
      const combo = this.#combos[`${input.value}|${opt2}`];
      input.closest('.noju-pdp__core-set-size')?.classList.toggle('is-unavailable', !combo?.available);
    });
    this.#getDimensionInputs(2).forEach((input) => {
      const combo = this.#combos[`${opt1}|${input.value}`];
      input.closest('.noju-pdp__core-set-size')?.classList.toggle('is-unavailable', !combo?.available);
    });

    [
      [1, opt1],
      [2, opt2],
    ].forEach(([dimension, size]) => {
      const label = this.querySelector(`[data-core-set-dimension="${dimension}"] [data-core-set-selected-size]`);
      if (label instanceof HTMLElement) {
        label.textContent = size ? String(size) : '';
        label.classList.toggle('is-set', Boolean(size));
      }
    });

    const combo = this.#getSelectedCombo();
    const submit = this.querySelector('[data-core-set-submit]');

    if (submit instanceof HTMLButtonElement) {
      submit.disabled = !combo?.available;
    }
    if (combo?.priceFormatted) {
      this.querySelectorAll('[data-core-set-price], [data-core-set-card-price]').forEach((price) => {
        price.textContent = combo.priceFormatted;
      });
    }
  }

  async #addToCart() {
    const combo = this.#getSelectedCombo();
    const submit = this.querySelector('[data-core-set-submit]');
    const errorEl = this.querySelector('[data-core-set-error]');

    if (!(submit instanceof HTMLButtonElement) || !combo?.available) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    submit.disabled = true;
    if (errorEl instanceof HTMLElement) {
      errorEl.classList.add('hidden');
      errorEl.textContent = '';
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: combo.id, quantity: 1 }],
            sections: sectionIds.join(','),
          }),
        }),
        signal: this.#abortController.signal,
      });

      const data = await response.json();

      if (data.status) {
        if (errorEl instanceof HTMLElement) {
          errorEl.textContent = data.message || data.description || 'Could not add bundle.';
          errorEl.classList.remove('hidden');
        }

        this.dispatchEvent(
          new CartErrorEvent(this.dataset.sectionId || '', data.message, data.description, data.errors)
        );
        return;
      }

      await this.#closeSheet();

      document.dispatchEvent(
        new CartAddEvent(data, this.dataset.sectionId || '', {
          source: 'noju-core-set-add',
          itemCount: 1,
          productId: this.dataset.productId,
          variantId: String(combo.id),
          sections: data.sections,
        })
      );
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
    } finally {
      submit.disabled = false;
    }
  }
}

if (!customElements.get('noju-core-set-add')) {
  customElements.define('noju-core-set-add', NojuCoreSetAdd);
}



/**
 * PDP buy box — size status labels + email notify signup.
 */
class NojuPdpBuybox {
  /** @type {HTMLElement | null} */
  #root = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {object | null} */
  #productData = null;

  /** @type {string | null} */
  #pendingVariantId = null;

  /** @type {number | undefined} */
  #atcFeedbackTimeout;

  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.#root = root;
    this.#productData = this.#readProductData();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const section = root.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.cartError, this.#onCartError, { signal });
    section?.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    root.addEventListener('click', this.#onClick, { signal });
    root.addEventListener('submit', this.#onSubmit, { signal });
    root.querySelector('form[data-type="add-to-cart-form"]')?.addEventListener(
      'submit',
      this.#onAddToCartFormSubmit,
      { signal, capture: true }
    );

    const dialog = root.querySelector('[data-noju-notify-dialog]');
    dialog?.addEventListener('click', this.#onDialogClick, { signal });
    dialog?.addEventListener('close', this.#onDialogClose, { signal });

    this.#syncSizeStatuses();

    const initialVariantId = this.#getVariantIdInput()?.value;
    if (this.#hasSizeSelected() && initialVariantId) {
      this.#setVariantIdInputEnabled(true);
      this.#syncSellingPlanForVariant(initialVariantId);
      this.#syncAtcForVariantId(initialVariantId);
    } else {
      this.#setVariantIdInputEnabled(false);
      this.#setAtcMode('select');
    }

    this.#syncStickyAtc();
  }

  destroy() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#root = null;
  }

  /** @param {VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    const variant = event.detail.resource;
    if (!variant?.id) return;

    this.#productData = this.#readProductData();
    this.#syncAtcPriceFromHtml(event.detail.data.html);
    this.#syncSellingPlanForVariant(String(variant.id));
    this.#syncCheckoutForVariant(variant);
    this.#syncAtcAfterVariantUpdate(String(variant.id));
  };

  /**
   * @param {Document | Element | null | undefined} html
   */
  #syncAtcPriceFromHtml(html) {
    const buybox = this.#root;
    if (!buybox || !html) return;

    const newPrice = html.querySelector('[data-noju-atc-price]');
    const atcPrice = buybox.querySelector('[data-noju-atc-price]');
    if (!(newPrice instanceof HTMLElement) || !(atcPrice instanceof HTMLElement)) return;

    atcPrice.textContent = newPrice.textContent;
    atcPrice.classList.toggle('hidden', newPrice.classList.contains('hidden'));
  }

  /**
   * Toggle dynamic checkout visibility on variant change only.
   * Initial state comes from server HTML — do not override on connect.
   * @param {{ available?: boolean } | null} variant
   */
  #syncCheckoutForVariant(variant) {
    const buybox = this.#root;
    if (!buybox) return;

    const container = buybox.querySelector('[data-noju-dynamic-checkout]');
    if (!(container instanceof HTMLElement)) return;

    const variantId = variant?.id ? String(variant.id) : '';
    const showCheckout = variantId ? this.#isVariantPurchasable(variantId) : Boolean(variant?.available);

    if (showCheckout) {
      container.removeAttribute('hidden');
    } else {
      container.setAttribute('hidden', 'true');
    }
  }

  /**
   * Re-sync ATC after Horizon morphs the buy box on variant change.
   * @param {string} variantId
   */
  #syncAtcAfterVariantUpdate(variantId) {
    const apply = () => {
      this.#syncSizeStatuses();
      if (this.#hasSizeSelected() && variantId) {
        this.#setVariantIdInputEnabled(true);
        this.#syncAtcForVariantId(variantId);
      } else {
        this.#setVariantIdInputEnabled(false);
        this.#setAtcMode('select');
      }
      this.#syncStickyAtc();
    };

    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
  }

  #syncStickyAtc() {
    const section = this.#root?.closest('.shopify-section');
    if (section instanceof HTMLElement) {
      syncNojuPdpStickyAtc(section);
    }
  }

  /** @param {Event} event */
  #onAddToCartFormSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const atcButton = this.#root?.querySelector('[data-noju-atc-button]');
    if (!(atcButton instanceof HTMLButtonElement) || atcButton.dataset.nojuAtcMode !== 'add' || atcButton.disabled) {
      return;
    }

    this.#setAtcFeedbackState('loading');
  };

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    if (event.detail?.data?.source !== 'product-form-component') return;

    const productId = event.detail?.data?.productId;
    if (productId && this.#productData?.id && String(productId) !== String(this.#productData.id)) return;

    this.#setAtcFeedbackState('success');

    if (typeof navigator.vibrate === 'function') {
      navigator.vibrate(12);
    }
  };

  /** @param {CustomEvent} event */
  #onCartError = (event) => {
    const buybox = this.#root;
    if (!buybox) return;

    const section = buybox.closest('.shopify-section');
    const sourceId = event.detail?.sourceId;
    if (sourceId && section && !String(sourceId).includes(section.dataset.sectionId || '')) {
      const productForm = buybox.querySelector('product-form-component');
      if (productForm && sourceId !== productForm.id) return;
    }

    this.#setAtcFeedbackState('idle');

    const message =
      event.detail?.data?.message ||
      event.detail?.data?.description ||
      buybox.dataset.nojuCartErrorGeneric ||
      'Could not add to bag. Please try again.';

    const errorEl = buybox.querySelector('[ref="addToCartTextError"]');
    if (errorEl instanceof HTMLElement) {
      errorEl.classList.remove('hidden');

      const textNode = errorEl.childNodes[2];
      if (textNode) {
        textNode.textContent = message;
      } else {
        errorEl.appendChild(document.createTextNode(message));
      }
    }
  };

  #syncSizeStatuses() {
    const buybox = this.#root;
    if (!buybox) return;

    const threshold = Number(buybox.dataset.lowStockThreshold) || 20;
    const fewLeftLabel = buybox.dataset.nojuFewLeftLabel || 'Few left';
    const notifyLabel = buybox.dataset.nojuNotifyLabel || 'Notify me';

    buybox.querySelectorAll('.noju-pdp__size-cell').forEach((cell) => {
      const input = cell.querySelector('input[data-variant-id]');
      let statusEl = cell.querySelector('.noju-pdp__size-status');
      if (!(input instanceof HTMLInputElement) || !statusEl) return;

      const managed = input.dataset.inventoryManaged === 'true';
      const qty = Number(input.dataset.inventoryQuantity);
      const available = input.dataset.optionAvailable !== 'false';
      const variantId = input.dataset.variantId || '';

      /** @type {'few_left' | 'notify' | 'empty'} */
      let type = 'empty';
      if (managed && qty > 0 && qty <= threshold) {
        type = 'few_left';
      } else if ((managed && qty <= 0) || !available) {
        type = 'notify';
      }

      const classSuffix = type === 'empty' ? 'empty' : type.replace('_', '-');
      const needsButton = type === 'notify';
      const isButton = statusEl.matches('[data-noju-notify-trigger]');

      if (needsButton !== isButton) {
        const newEl = document.createElement(needsButton ? 'button' : 'span');
        newEl.className = `noju-pdp__size-status noju-pdp__size-status--${classSuffix}`;
        if (needsButton) {
          newEl.type = 'button';
          newEl.dataset.nojuNotifyTrigger = '';
          if (variantId) {
            newEl.dataset.variantId = variantId;
            const notifyMessage = this.#getNotifyMessage(variantId);
            if (notifyMessage) newEl.dataset.nojuNotifyMessage = notifyMessage;
          }
        } else if (type === 'empty') {
          newEl.setAttribute('aria-hidden', 'true');
        }
        statusEl.replaceWith(newEl);
        statusEl = newEl;
      }

      statusEl.className = `noju-pdp__size-status noju-pdp__size-status--${classSuffix}`;
      input.dataset.sizeStatusType = type === 'empty' ? '' : type;

      if (type === 'few_left') {
        statusEl.textContent = fewLeftLabel;
      } else if (type === 'notify') {
        statusEl.textContent = notifyLabel;
        if (statusEl instanceof HTMLButtonElement && variantId) {
          statusEl.dataset.variantId = variantId;
          const notifyMessage = this.#getNotifyMessage(variantId);
          if (notifyMessage) statusEl.dataset.nojuNotifyMessage = notifyMessage;
        }
      } else {
        statusEl.innerHTML = '&nbsp;';
      }
    });
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const notifyTrigger = target.closest('[data-noju-notify-trigger]');
    if (notifyTrigger instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();

      const variantId =
        notifyTrigger.dataset.variantId || this.#getVariantIdInput()?.value || this.#getCheckedVariantId();

      if (variantId) {
        this.#openNotifyModal(variantId);
      }
      return;
    }

    const atcButton = target.closest('[data-noju-atc-button]');
    if (atcButton instanceof HTMLButtonElement && atcButton.dataset.nojuAtcMode === 'notify') {
      event.preventDefault();
      event.stopImmediatePropagation();

      const variantId = this.#getVariantIdInput()?.value || this.#getCheckedVariantId();
      if (variantId) {
        this.#openNotifyModal(variantId);
      }
    }
  };

  /** @returns {boolean} */
  #hasSizeSelected() {
    return Boolean(this.#root?.querySelector('.noju-pdp__size-options input:checked'));
  }

  /** @param {boolean} enabled */
  #setVariantIdInputEnabled(enabled) {
    const variantInput = this.#getVariantIdInput();
    if (!(variantInput instanceof HTMLInputElement)) return;

    variantInput.disabled = !enabled;
    if (!enabled) {
      variantInput.value = '';
    }
  }

  /** @param {string} variantId */
  #syncAtcForVariantId(variantId) {
    if (!this.#hasSizeSelected() || !variantId) {
      this.#setVariantIdInputEnabled(false);
      this.#setAtcMode('select');
      return;
    }

    this.#setVariantIdInputEnabled(true);

    if (this.#isSellingPlanBlocked(variantId)) {
      this.#setAtcMode('blocked', variantId);
      return;
    }

    this.#setAtcMode(this.#isVariantPurchasable(variantId) ? 'add' : 'notify', variantId);
  }

  /**
   * @param {string} variantId
   * @returns {boolean}
   */
  #isSellingPlanBlocked(variantId) {
    const variant = this.#findVariant(variantId);
    if (!variant?.requires_selling_plan) return false;

    const allocations = variant.selling_plan_allocations;
    return !Array.isArray(allocations) || allocations.length === 0;
  }

  /**
   * @param {string} variantId
   * @returns {boolean}
   */
  #isVariantPurchasable(variantId) {
    if (this.#isSellingPlanBlocked(variantId)) return false;

    const input = this.#root?.querySelector(`input[data-variant-id="${variantId}"]`);
    if (input instanceof HTMLInputElement) {
      const managed = input.dataset.inventoryManaged === 'true';
      const qty = Number(input.dataset.inventoryQuantity);
      if (managed && qty <= 0) return false;
      return input.dataset.optionAvailable !== 'false';
    }

    const variant = this.#findVariant(variantId);
    return variant ? Boolean(variant.available) : false;
  }

  /**
   * @param {'add' | 'notify' | 'select' | 'blocked'} mode
   * @param {string} [_variantId]
   */
  #setAtcMode(mode, _variantId) {
    const buybox = this.#root;
    if (!buybox) return;

    const atcButton = buybox.querySelector('[data-noju-atc-button]');
    const atcLabel = buybox.querySelector('[data-noju-atc-label]');
    const atcPrice = buybox.querySelector('[data-noju-atc-price]');
    const addToCartContainer = buybox.querySelector('add-to-cart-component');

    if (!(atcButton instanceof HTMLButtonElement) || !(atcLabel instanceof HTMLElement)) return;

    const addLabel = buybox.dataset.nojuAddLabel || 'Add product to bag';
    const notifyLabel = buybox.dataset.nojuNotifyLabel || 'Notify me';
    const selectSizeLabel = buybox.dataset.nojuSelectSizeLabel || 'Select size';
    const sellingPlanMissingLabel =
      buybox.dataset.nojuSellingPlanMissingLabel || 'This product is not available to purchase right now.';

    if (mode === 'select') {
      atcButton.type = 'button';
      atcButton.disabled = true;
      atcButton.dataset.nojuAtcMode = 'select';
      atcLabel.textContent = selectSizeLabel;
      atcPrice?.classList.add('hidden');
      this.#setAddToCartContainerEnabled(addToCartContainer, false);
      this.#syncCheckoutForVariant(null);
      this.#syncStickyAtc();
      return;
    }

    if (_variantId && this.#isSellingPlanBlocked(_variantId)) {
      atcButton.type = 'button';
      atcButton.disabled = true;
      atcButton.dataset.nojuAtcMode = 'blocked';
      atcLabel.textContent = sellingPlanMissingLabel;
      atcPrice?.classList.add('hidden');
      this.#setAddToCartContainerEnabled(addToCartContainer, false);
      this.#syncStickyAtc();
      return;
    }

    if (mode === 'notify') {
      atcButton.type = 'button';
      atcButton.disabled = false;
      atcButton.dataset.nojuAtcMode = 'notify';
      atcLabel.textContent = notifyLabel;
      atcPrice?.classList.add('hidden');
      this.#setAddToCartContainerEnabled(addToCartContainer, true);
      this.#syncStickyAtc();
      return;
    }

    atcButton.type = 'submit';
    atcButton.dataset.nojuAtcMode = 'add';
    atcLabel.textContent = addLabel;
    atcPrice?.classList.remove('hidden');

    const isAvailable = _variantId ? this.#isVariantPurchasable(_variantId) : true;

    atcButton.disabled = !isAvailable;
    this.#setAddToCartContainerEnabled(addToCartContainer, isAvailable);
    this.#syncStickyAtc();
  }

  /**
   * @param {string} variantId
   */
  #syncSellingPlanForVariant(variantId) {
    const buybox = this.#root;
    if (!buybox) return;

    const input = buybox.querySelector('[data-noju-selling-plan-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const variant = this.#findVariant(variantId);
    const allocations = variant?.selling_plan_allocations;
    const firstPlanId =
      Array.isArray(allocations) && allocations.length > 0
        ? String(allocations[0].selling_plan_id || allocations[0].selling_plan?.id || '')
        : '';

    if (firstPlanId) {
      input.value = firstPlanId;
      input.disabled = false;
      return;
    }

    input.value = '';
    input.disabled = true;
  }

  /** @param {'idle' | 'loading' | 'success'} state */
  #setAtcFeedbackState(state) {
    const buybox = this.#root;
    if (!buybox) return;

    if (this.#atcFeedbackTimeout) {
      clearTimeout(this.#atcFeedbackTimeout);
      this.#atcFeedbackTimeout = undefined;
    }

    const section = buybox.closest('.shopify-section');
    const buttons = /** @type {HTMLButtonElement[]} */ (
      [
        buybox.querySelector('[data-noju-atc-button]'),
        section?.querySelector('[data-noju-sticky-atc-button]'),
      ].filter((button) => button instanceof HTMLButtonElement)
    );

    if (buttons.length === 0) return;

    const addingLabel = buybox.dataset.nojuAddingLabel || 'Adding…';
    const addedLabel = buybox.dataset.nojuAddedLabel || 'Added to bag ✓';

    if (state === 'idle') {
      buttons.forEach((button) => {
        button.removeAttribute('data-noju-atc-state');
        button.disabled = false;
      });

      const variantId = this.#getVariantIdInput()?.value;
      if (variantId) {
        this.#syncAtcForVariantId(variantId);
      }
      return;
    }

    buttons.forEach((button) => {
      button.dataset.nojuAtcState = state;
      const label = button.querySelector('[data-noju-atc-label], [data-noju-sticky-atc-label]');
      const price = button.querySelector('[data-noju-atc-price], [data-noju-sticky-atc-price]');

      if (!(label instanceof HTMLElement)) return;

      if (state === 'loading') {
        label.textContent = addingLabel;
        price?.classList.add('hidden');
        button.disabled = true;
        return;
      }

      label.textContent = addedLabel;
      price?.classList.add('hidden');
      button.disabled = false;
    });

    if (state === 'success') {
      this.#atcFeedbackTimeout = window.setTimeout(() => {
        this.#setAtcFeedbackState('idle');
      }, 1400);
    }
  }

  /**
   * @param {Element | null} container
   * @param {boolean} enabled
   */
  #setAddToCartContainerEnabled(container, enabled) {
    if (!container || typeof container !== 'object') return;

    const component = /** @type {{ enable?: () => void; disable?: () => void }} */ (container);
    if (enabled && typeof component.enable === 'function') {
      component.enable();
    } else if (!enabled && typeof component.disable === 'function') {
      component.disable();
    }
  }

  /** @param {Event} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-noju-notify-form]')) return;

    event.preventDefault();

    const buybox = this.#root;
    if (!buybox) return;

    const emailInput = form.querySelector('[data-noju-notify-email]');
    const submitButton = form.querySelector('[data-noju-notify-submit]');
    const feedback = buybox.querySelector('[data-noju-notify-feedback]');

    if (!(emailInput instanceof HTMLInputElement) || !(submitButton instanceof HTMLButtonElement)) return;

    const email = emailInput.value.trim();
    const invalidMessage = buybox.dataset.nojuNotifyInvalidEmail || 'Please enter a valid email address.';

    if (!email || !email.includes('@')) {
      this.#setNotifyFeedback(feedback, invalidMessage, true);
      emailInput.focus();
      return;
    }

    const variantId = this.#pendingVariantId || this.#getVariantIdInput()?.value;
    const variant = variantId ? this.#findVariant(variantId) : undefined;
    const productData = this.#productData;

    submitButton.disabled = true;

    try {
      await this.#submitNotifySignup({
        email,
        variantId: variantId ? String(variantId) : '',
        variantTitle: variant?.title || variant?.public_title || '',
        productTitle: productData?.title || '',
        productHandle: productData?.handle || '',
        productId: productData?.id ? String(productData.id) : '',
      });

      const successTemplate = buybox.dataset.nojuNotifySuccess || "You're on the list.";
      const successMessage = variantId
        ? this.#formatNotifyMessage(successTemplate, String(variantId))
        : successTemplate;
      this.#setNotifyFeedback(feedback, successMessage, false);
      form.classList.add('hidden');
    } catch (error) {
      console.error(error);
      const errorMessage = buybox.dataset.nojuNotifyError || 'Something went wrong. Please try again.';
      this.#setNotifyFeedback(feedback, errorMessage, true);
    } finally {
      submitButton.disabled = false;
    }
  };

  /** @param {Event} event */
  #onDialogClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.matches('[data-noju-notify-close]')) {
      event.preventDefault();
      this.#closeNotifyDialog();
      return;
    }

    if (target.matches('[data-noju-notify-dialog]')) {
      this.#closeNotifyDialog();
    }
  };

  #onDialogClose = () => {
    this.#resetNotifyDialog();
  };

  /**
   * @param {Element | null} feedback
   * @param {string} message
   * @param {boolean} isError
   */
  #setNotifyFeedback(feedback, message, isError) {
    if (!(feedback instanceof HTMLElement)) return;

    feedback.textContent = message;
    feedback.classList.remove('hidden');
    feedback.classList.toggle('noju-pdp__notify-feedback--error', isError);
    feedback.classList.toggle('noju-pdp__notify-feedback--success', !isError);
  }

  #resetNotifyDialog() {
    const buybox = this.#root;
    if (!buybox) return;

    const form = buybox.querySelector('[data-noju-notify-form]');
    const feedback = buybox.querySelector('[data-noju-notify-feedback]');
    const emailInput = buybox.querySelector('[data-noju-notify-email]');

    form?.classList.remove('hidden');
    feedback?.classList.add('hidden');
    if (feedback instanceof HTMLElement) {
      feedback.textContent = '';
      feedback.classList.remove('noju-pdp__notify-feedback--error', 'noju-pdp__notify-feedback--success');
    }
    if (emailInput instanceof HTMLInputElement) {
      emailInput.value = '';
    }

    this.#pendingVariantId = null;
  }

  /**
   * @param {string} template
   * @param {string} variantId
   * @returns {string}
   */
  #formatNotifyMessage(template, variantId) {
    const sizeLabel = this.#getVariantLabel(variantId);
    const fallback = this.#decodeHtmlEntities(this.#root?.dataset.nojuNotifyVariantFallback || 'this size');

    return this.#decodeHtmlEntities(template).replace(
      /\{\{\s*(variant|size)\s*\}\}/g,
      sizeLabel || fallback
    );
  }

  /**
   * @param {string | number} variantId
   * @returns {string}
   */
  #getVariantLabel(variantId) {
    const input = this.#root?.querySelector(`input[data-variant-id="${variantId}"]`);
    if (input instanceof HTMLInputElement) {
      const sizeText = input
        .closest('.noju-pdp__size-cell')
        ?.querySelector('[data-key="variant-option-text"]')
        ?.textContent?.trim();
      if (sizeText) return sizeText;
    }

    const variant = this.#findVariant(variantId);
    return variant?.option1 || variant?.title || variant?.public_title || '';
  }

  /**
   * @param {string | number} variantId
   * @returns {string}
   */
  #getNotifyMessage(variantId) {
    const variantKey = String(variantId);
    const trigger = this.#root?.querySelector(
      `[data-noju-notify-trigger][data-variant-id="${variantKey}"]`
    );
    if (trigger instanceof HTMLElement && trigger.dataset.nojuNotifyMessage) {
      return this.#decodeHtmlEntities(trigger.dataset.nojuNotifyMessage);
    }

    const input = this.#root?.querySelector(`input[data-variant-id="${variantKey}"]`);
    if (input instanceof HTMLInputElement && input.dataset.nojuNotifyMessage) {
      return this.#decodeHtmlEntities(input.dataset.nojuNotifyMessage);
    }

    const template = this.#root?.dataset.nojuNotifyDescription || '';
    return template ? this.#formatNotifyMessage(template, variantKey) : '';
  }

  /** @param {string} value */
  #decodeHtmlEntities(value) {
    if (!value.includes('&')) return value;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  /** @param {string | number} variantId */
  #openNotifyModal(variantId) {
    const buybox = this.#root;
    if (!buybox) return;

    const dialog = buybox.querySelector('[data-noju-notify-dialog]');
    const description = buybox.querySelector('[data-noju-notify-description]');

    this.#resetNotifyDialog();
    this.#pendingVariantId = String(variantId);

    if (description instanceof HTMLElement) {
      description.textContent = this.#getNotifyMessage(variantId);
    }

    if (dialog instanceof HTMLDialogElement) {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      const emailInput = buybox.querySelector('[data-noju-notify-email]');
      if (emailInput instanceof HTMLInputElement) {
        requestAnimationFrame(() => emailInput.focus());
      }
    }
  }

  #closeNotifyDialog() {
    const dialog = this.#root?.querySelector('[data-noju-notify-dialog]');
    if (dialog instanceof HTMLDialogElement) {
      dialog.close();
    }
  }

  /**
   * @param {object} payload
   * @param {string} payload.email
   * @param {string} payload.variantId
   * @param {string} payload.variantTitle
   * @param {string} payload.productTitle
   * @param {string} payload.productHandle
   * @param {string} payload.productId
   */
  async #submitNotifySignup(payload) {
    const tags = ['notify-me', payload.productHandle, `variant-${payload.variantId}`]
      .filter(Boolean)
      .join(', ');

    const formData = new URLSearchParams({
      form_type: 'customer',
      utf8: '✓',
      'contact[email]': payload.email,
      'customer[accepts_marketing]': 'true',
      'contact[tags]': tags,
    });

    const response = await fetch('/contact', {
      method: 'POST',
      headers: {
        Accept: 'text/html',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Customer signup failed with status ${response.status}`);
    }
  }

  /** @returns {HTMLInputElement | null} */
  #getVariantIdInput() {
    return this.#root?.querySelector('input[name="id"]') ?? null;
  }

  /** @returns {string | undefined} */
  #getCheckedVariantId() {
    const checked = this.#root?.querySelector('.noju-pdp__size-options input:checked');
    if (checked instanceof HTMLInputElement) {
      return checked.dataset.variantId;
    }
    return undefined;
  }

  /**
   * @param {string} variantId
   * @returns {object | undefined}
   */
  #findVariant(variantId) {
    const variants = this.#productData?.variants;
    if (!Array.isArray(variants)) return undefined;

    return variants.find((variant) => String(variant.id) === String(variantId));
  }

  /** @returns {object | null} */
  #readProductData() {
    const script = this.#root?.querySelector('[data-noju-product-json]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('NOJU PDP: could not parse product JSON', error);
      return null;
    }
  }
}

/** @type {Map<HTMLElement, NojuPdpBuybox>} */
const buyboxControllers = new Map();

function initNojuPdpBuyboxes() {
  document.querySelectorAll('[data-noju-buybox]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    if (buyboxControllers.has(root)) return;
    buyboxControllers.set(root, new NojuPdpBuybox(root));
  });
}

function destroyNojuPdpBuybox(root) {
  const controller = buyboxControllers.get(root);
  if (!controller) return;
  controller.destroy();
  buyboxControllers.delete(root);
}

/**
 * Details carousel — drag, wheel, arrows, and scroll clamp.
 * @param {ParentNode} [scope]
 */
function initNojuPdpDetailsTracks(scope = document) {
  scope.querySelectorAll('[data-noju-pdp-details-track]').forEach((track) => {
    if (!(track instanceof HTMLElement) || track.dataset.nojuDetailsInit === 'true') return;
    track.dataset.nojuDetailsInit = 'true';

    const carousel = track.closest('.noju-pdp__details-carousel');
    const prevButton = carousel?.querySelector('[data-noju-pdp-details-prev]');
    const nextButton = carousel?.querySelector('[data-noju-pdp-details-next]');

    /** @type {HTMLElement[]} */
    const slides = [...track.querySelectorAll('.noju-pdp__details-slide')].filter(
      (slide) => slide instanceof HTMLElement
    );

    slides.forEach((slide) => {
      slide.querySelectorAll('img').forEach((image) => {
        image.setAttribute('draggable', 'false');
      });
    });

    const isHorizontalCarousel = () => getComputedStyle(track).flexDirection === 'row';

    const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const clampScrollLeft = (value) => {
      const maxScroll = getMaxScroll();
      return Math.min(maxScroll, Math.max(0, value));
    };

    const getSlideTarget = (index) => {
      const maxScroll = getMaxScroll();
      if (index <= 0) return 0;
      if (index >= slides.length - 1) return maxScroll;
      return clampScrollLeft(slides[index].offsetLeft);
    };

    const getActiveSlideIndex = () => {
      if (slides.length === 0) return 0;

      const maxScroll = getMaxScroll();
      const scrollLeft = track.scrollLeft;

      if (scrollLeft >= maxScroll - 6) return slides.length - 1;
      if (scrollLeft <= 6) return 0;

      let closest = 0;
      let closestDistance = Infinity;

      slides.forEach((_slide, index) => {
        const distance = Math.abs(getSlideTarget(index) - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = index;
        }
      });

      return closest;
    };

    const scrollToSlide = (index, behavior = 'smooth') => {
      if (!isHorizontalCarousel() || !slides[index]) return;
      track.scrollTo({ left: getSlideTarget(index), behavior });
    };

    const updateNavButtons = () => {
      if (!isHorizontalCarousel()) {
        if (prevButton instanceof HTMLButtonElement) prevButton.disabled = true;
        if (nextButton instanceof HTMLButtonElement) nextButton.disabled = true;
        return;
      }

      const activeIndex = getActiveSlideIndex();
      if (prevButton instanceof HTMLButtonElement) {
        prevButton.disabled = slides.length <= 1 || activeIndex <= 0;
      }
      if (nextButton instanceof HTMLButtonElement) {
        nextButton.disabled = slides.length <= 1 || activeIndex >= slides.length - 1;
      }
    };

    let settleTimer = 0;
    let isTouching = false;
    let isDragging = false;
    let dragLocked = false;

    const settleScroll = () => {
      if (!isHorizontalCarousel() || slides.length === 0 || isDragging || dragLocked || isTouching) return;

      const scrollLeft = clampScrollLeft(track.scrollLeft);
      const target = getSlideTarget(getActiveSlideIndex());

      if (Math.abs(scrollLeft - target) > 2) {
        track.scrollTo({ left: target, behavior: 'auto' });
      } else if (scrollLeft !== track.scrollLeft) {
        track.scrollLeft = scrollLeft;
      }

      updateNavButtons();
    };

    const scheduleSettle = (delay = 80) => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settleScroll, delay);
    };

    const scrollBySlide = (direction) => {
      if (!isHorizontalCarousel() || slides.length === 0) return;

      const nextIndex = Math.min(slides.length - 1, Math.max(0, getActiveSlideIndex() + direction));
      scrollToSlide(nextIndex);
    };

    prevButton?.addEventListener('click', () => scrollBySlide(-1));
    nextButton?.addEventListener('click', () => scrollBySlide(1));

    track.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollBySlide(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollBySlide(1);
        }
      },
      { passive: false }
    );

    let dragStartX = 0;
    let dragStartY = 0;
    let dragScrollStart = 0;
    let suppressClick = false;
    const dragThreshold = 8;

    track.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      if (event.pointerType !== 'mouse') return;
      if (!isHorizontalCarousel()) return;
      if (event.target instanceof Element && event.target.closest('a, button')) return;
      if (getMaxScroll() <= 0) return;

      isDragging = true;
      dragLocked = false;
      suppressClick = false;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      dragScrollStart = track.scrollLeft;
    });

    track.addEventListener(
      'pointermove',
      (event) => {
        if (!isDragging) return;

        const deltaX = event.clientX - dragStartX;
        const deltaY = event.clientY - dragStartY;

        if (!dragLocked) {
          if (Math.abs(deltaX) < dragThreshold && Math.abs(deltaY) < dragThreshold) return;

          if (Math.abs(deltaY) > Math.abs(deltaX)) {
            isDragging = false;
            return;
          }

          dragLocked = true;
          track.classList.add('is-dragging');
          track.setPointerCapture(event.pointerId);
        }

        if (Math.abs(deltaX) > 3) suppressClick = true;
        track.scrollLeft = clampScrollLeft(dragScrollStart - deltaX);
      },
      { passive: true }
    );

    const endDrag = (event) => {
      if (!isDragging && !dragLocked) return;
      isDragging = false;
      dragLocked = false;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }
      scheduleSettle(50);
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    track.addEventListener(
      'touchstart',
      () => {
        isTouching = true;
        window.clearTimeout(settleTimer);
      },
      { passive: true }
    );

    track.addEventListener(
      'touchend',
      () => {
        isTouching = false;
        scheduleSettle(100);
      },
      { passive: true }
    );

    track.addEventListener(
      'touchcancel',
      () => {
        isTouching = false;
        scheduleSettle(100);
      },
      { passive: true }
    );

    track.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      },
      true
    );

    track.addEventListener(
      'wheel',
      (event) => {
        if (!isHorizontalCarousel()) return;

        const maxScroll = getMaxScroll();
        if (maxScroll <= 0) return;

        const absX = Math.abs(event.deltaX);
        const absY = Math.abs(event.deltaY);

        // Touchpad sideways, shift+vertical wheel, or dominant horizontal delta.
        let delta = 0;
        if (absX > 0.5 && absX >= absY) {
          delta = event.deltaX;
        } else if (event.shiftKey && absY > 0) {
          delta = event.deltaY;
        } else {
          return;
        }

        const next = clampScrollLeft(track.scrollLeft + delta);
        if (next === track.scrollLeft && ((delta < 0 && track.scrollLeft <= 0) || (delta > 0 && track.scrollLeft >= maxScroll - 0.5))) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        track.scrollLeft = next;
        // Longer debounce so trackpad momentum is not snappped mid-gesture.
        scheduleSettle(200);
      },
      { passive: false }
    );

    const onScroll = () => {
      const maxScroll = getMaxScroll();
      if (track.scrollLeft < 0 || track.scrollLeft > maxScroll) {
        track.scrollLeft = clampScrollLeft(track.scrollLeft);
      }
      updateNavButtons();
      if (!isDragging && !dragLocked) {
        scheduleSettle(isTouching ? 160 : 120);
      }
    };

    const onResize = () => {
      updateNavButtons();
      scheduleSettle(0);
    };

    track.addEventListener('scroll', onScroll, { passive: true });

    if ('onscrollend' in window) {
      track.addEventListener(
        'scrollend',
        () => {
          if (!isDragging && !dragLocked && !isTouching) {
            scheduleSettle(0);
          }
        },
        { passive: true }
      );
    }

    window.addEventListener('resize', onResize, { passive: true });
    updateNavButtons();
  });
}

/**
 * Collapse long product-detail copy with fade + Read more.
 * @param {ParentNode} [scope]
 */
function initNojuPdpReadmore(scope = document) {
  scope.querySelectorAll('[data-noju-readmore]').forEach((root) => {
    if (!(root instanceof HTMLElement) || root.dataset.nojuReadmoreInit === 'true') return;
    root.dataset.nojuReadmoreInit = 'true';

    const clip = root.querySelector('[data-noju-readmore-clip]');
    const content = root.querySelector('[data-noju-readmore-content]');
    const button = root.querySelector('[data-noju-readmore-btn]');
    const label = root.querySelector('[data-noju-readmore-label]');

    if (
      !(clip instanceof HTMLElement) ||
      !(content instanceof HTMLElement) ||
      !(button instanceof HTMLButtonElement)
    ) {
      return;
    }

    const moreText = root.dataset.readMoreLabel || 'Read more';
    const lessText = root.dataset.showLessLabel || 'Show less';
    let expanded = false;

    const measure = () => {
      if (expanded) return;

      root.classList.remove('is-collapsed');
      const fullHeight = content.scrollHeight;
      root.classList.add('is-collapsed');
      const collapsedHeight = clip.clientHeight;
      const needsClamp = fullHeight > collapsedHeight + 8;

      button.hidden = !needsClamp;
      if (!needsClamp) {
        root.classList.remove('is-collapsed');
        button.setAttribute('aria-expanded', 'false');
        if (label instanceof HTMLElement) label.textContent = moreText;
      }
    };

    const setExpanded = (next) => {
      expanded = next;
      button.setAttribute('aria-expanded', String(next));
      root.classList.toggle('is-collapsed', !next);
      if (label instanceof HTMLElement) {
        label.textContent = next ? lessText : moreText;
      }
      if (!next) measure();
    };

    button.addEventListener('click', () => setExpanded(!expanded));
    requestAnimationFrame(measure);
    window.addEventListener('resize', measure, { passive: true });
  });
}

initNojuPdpDetailsTracks();
initNojuPdpBuyboxes();
initNojuPdpReadmore();

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  initNojuPdpDetailsTracks(event.target);
  initNojuPdpReadmore(event.target);
  event.target.querySelectorAll('[data-noju-buybox]').forEach((root) => {
    if (root instanceof HTMLElement) {
      destroyNojuPdpBuybox(root);
      buyboxControllers.set(root, new NojuPdpBuybox(root));
    }
  });
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  event.target.querySelectorAll('[data-noju-buybox]').forEach((root) => {
    if (root instanceof HTMLElement) {
      destroyNojuPdpBuybox(root);
    }
  });
});

